<template>
  <div class="label-workspace" @mousedown.self="$emit('select', null)">
    <div class="workspace-toolbar">
      <div class="d-flex align-items-center">
        <span class="live-dot"></span>
        <strong>Área de edição instantânea</strong>
        <span class="text-muted ml-2">Dados de exemplo</span>
      </div>
      <div class="d-flex align-items-center">
        <span class="text-muted mr-3"
          >Grade de 0,5 mm · Área segura {{ safePadding }} mm · Margens da folha
          {{ pageMarginSummary }}</span
        >
        <span class="badge badge-light"
          >{{ labelWidth }} × {{ labelHeight }} mm</span
        >
      </div>
    </div>

    <div class="canvas-scroll" @mousedown.self="$emit('select', null)">
      <div
        class="canvas-stage"
        :style="stageStyle"
        @mousedown.self="$emit('select', null)"
      >
        <div class="ruler ruler-x" :style="rulerXStyle">
          <span
            v-for="mark in xMarks"
            :key="`x-${mark}`"
            :style="{ left: `${mark * scale}px` }"
            >{{ mark }}</span
          >
        </div>
        <div class="ruler ruler-y" :style="rulerYStyle">
          <span
            v-for="mark in yMarks"
            :key="`y-${mark}`"
            :style="{ top: `${mark * scale}px` }"
            >{{ mark }}</span
          >
        </div>

        <div
          v-for="band in pageMarginBands"
          :key="band.side"
          :class="['page-margin-band', `margin-${band.side}`]"
          :style="band.style"
          aria-hidden="true"
        >
          <span>Margem da folha · {{ formatMillimeters(band.size) }} mm</span>
        </div>

        <div
          ref="label"
          class="label-canvas"
          :class="{ 'has-grid': showGrid }"
          :style="canvasStyle"
          @mousedown.self="$emit('select', null)"
        >
          <div
            v-if="safePadding > 0"
            class="safe-area-shade"
            :style="safeAreaStyle"
            aria-hidden="true"
          ></div>
          <div class="safe-area" :style="safeAreaStyle"></div>

          <div
            v-for="element in renderedElements"
            :key="element.id"
            class="label-element"
            :class="elementClasses(element)"
            :style="elementStyle(element)"
            role="button"
            :aria-label="`Selecionar ${sourceElement(element).nome}`"
            :tabindex="element.visivel === false ? -1 : 0"
            @mousedown.stop.prevent="beginDrag($event, element)"
            @click.stop="$emit('select', sourceElementId(element))"
            @keydown.enter.stop="$emit('select', sourceElementId(element))"
          >
            <template v-if="elementContentVisible(element)">
              <template v-if="previewElementType(element) === 'barcode'">
                <div v-if="codeAssetUrl(element)" class="code-preview">
                  <img
                    class="code-image"
                    alt="Código de barras"
                    :src="codeAssetUrl(element)"
                    :style="barcodeImageStyle(element)"
                  />
                  <small
                    v-if="
                      element.codigo_barras &&
                      element.codigo_barras.exibir_texto
                    "
                  >
                    {{ rawValue(element) }}
                  </small>
                </div>
                <span
                  v-else-if="codeAssetFailed(element)"
                  class="code-preview-error"
                  >Prévia indisponível</span
                >
              </template>

              <template v-else-if="previewElementType(element) === 'qrcode'">
                <img
                  v-if="codeAssetUrl(element)"
                  class="qr-preview"
                  alt="QR Code"
                  :src="codeAssetUrl(element)"
                />
                <span
                  v-else-if="codeAssetFailed(element)"
                  class="code-preview-error"
                  >Prévia indisponível</span
                >
              </template>

              <template v-else-if="previewElementType(element) === 'logo'">
                <img
                  v-if="logoUrl"
                  class="logo-preview"
                  alt="Logo da empresa"
                  :src="logoUrl"
                />
              </template>

              <template v-else-if="previewElementType(element) === 'line'">
                <span
                  class="line-preview"
                  :style="linePreviewStyle(element)"
                ></span>
              </template>

              <template
                v-else-if="previewElementType(element) === 'rectangle'"
              ></template>

              <template v-else>
                <span v-if="priceLines(element)" class="element-text">
                  <span
                    v-for="(line, index) in priceLines(element)"
                    :key="index"
                    class="element-text-line"
                    :style="priceLineStyle(element, line)"
                    >{{ line }}</span
                  >
                </span>
                <span v-else class="element-text">{{
                  displayValue(element)
                }}</span>
              </template>
            </template>

            <span v-if="element.visivel === false" class="hidden-label"
              >Oculto</span
            >
            <span
              v-else-if="
                selectedId === sourceElementId(element) &&
                !elementContentVisible(element)
              "
              class="hidden-label"
              >Não será impresso</span
            >
            <template v-if="selectedId === sourceElementId(element)">
              <span
                v-if="isFirstRenderedPart(element)"
                class="selection-name"
                >{{ sourceElement(element).nome }}</span
              >
              <span
                v-if="isLastRenderedPart(element)"
                class="resize-handle"
                title="Redimensionar"
                @mousedown.stop.prevent="beginResize($event, element)"
              ></span>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div class="workspace-help">
      <span><i class="fas fa-mouse-pointer"></i> Arraste para mover</span>
      <span
        ><i class="fas fa-expand-alt"></i> Puxe o canto para redimensionar</span
      >
      <span><kbd>Del</kbd> remove</span>
      <span><kbd>Ctrl</kbd> + <kbd>Z</kbd> desfaz</span>
    </div>
  </div>
</template>

<script>
import etiquetasService from "@/modules/Etiquetas/services/etiquetasService";
import {
  clone,
  previewValue,
  priceDisplayLines,
  resolveElementForPrintOptions,
  resolveElementsForPrintOptions,
  resolvePreviewValues,
  shouldDisplayPreview,
} from "@/modules/Etiquetas/services/etiquetaDefinition";

const codeAssetCache = new Map();
const logoAssetCache = new Map();
const MAX_CODE_ASSETS = 60;

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const rememberAsset = (cache, key, promise, maximum = MAX_CODE_ASSETS) => {
  if (cache.size >= maximum) cache.delete(cache.keys().next().value);
  cache.set(key, promise);
  promise.catch(() => cache.delete(key));
  return promise;
};

export default {
  name: "EtiquetaLabelCanvas",
  props: {
    definition: {
      type: Object,
      required: true,
    },
    sampleValues: {
      type: Object,
      default: () => ({}),
    },
    selectedId: {
      type: String,
      default: null,
    },
    zoom: {
      type: Number,
      default: 100,
    },
    showGrid: {
      type: Boolean,
      default: true,
    },
    testOptions: {
      type: Object,
      default: () => ({}),
    },
    exampleCompanyId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      interaction: null,
      codeAssets: {},
      codeAssetErrors: {},
      codeAssetTimer: null,
      codeAssetGeneration: 0,
      logoUrl: "",
      companyValues: {},
      logoGeneration: 0,
    };
  },
  computed: {
    page() {
      return this.definition.pagina || {};
    },
    label() {
      return this.definition.etiqueta || {};
    },
    labelWidth() {
      return Number(this.label.largura_mm || 50);
    },
    labelHeight() {
      return Number(this.label.altura_mm || 30);
    },
    scale() {
      return 5.2 * Math.max(0.5, Math.min(2, Number(this.zoom || 100) / 100));
    },
    resolvedValues() {
      return resolvePreviewValues(
        { ...this.sampleValues, ...this.companyValues },
        this.testOptions
      );
    },
    pageMargins() {
      return {
        top: Math.max(0, Number(this.page.margem_superior_mm || 0)),
        right: Math.max(0, Number(this.page.margem_direita_mm || 0)),
        bottom: Math.max(0, Number(this.page.margem_inferior_mm || 0)),
        left: Math.max(0, Number(this.page.margem_esquerda_mm || 0)),
      };
    },
    pageMarginSummary() {
      const margin = this.pageMargins;
      return `${this.formatMillimeters(margin.top)} / ${this.formatMillimeters(
        margin.right
      )} / ${this.formatMillimeters(margin.bottom)} / ${this.formatMillimeters(
        margin.left
      )} mm`;
    },
    canvasOffset() {
      return {
        left: 40 + this.pageMargins.left * this.scale,
        top: 30 + this.pageMargins.top * this.scale,
      };
    },
    pageMarginBands() {
      const margin = this.pageMargins;
      const left = 40;
      const top = 30;
      const totalWidth =
        (margin.left + this.labelWidth + margin.right) * this.scale;
      const labelHeight = this.labelHeight * this.scale;
      const labelLeft = this.canvasOffset.left;
      const labelTop = this.canvasOffset.top;

      return [
        {
          side: "top",
          size: margin.top,
          style: {
            left: `${left}px`,
            top: `${top}px`,
            width: `${totalWidth}px`,
            height: `${margin.top * this.scale}px`,
          },
        },
        {
          side: "right",
          size: margin.right,
          style: {
            left: `${labelLeft + this.labelWidth * this.scale}px`,
            top: `${labelTop}px`,
            width: `${margin.right * this.scale}px`,
            height: `${labelHeight}px`,
          },
        },
        {
          side: "bottom",
          size: margin.bottom,
          style: {
            left: `${left}px`,
            top: `${labelTop + labelHeight}px`,
            width: `${totalWidth}px`,
            height: `${margin.bottom * this.scale}px`,
          },
        },
        {
          side: "left",
          size: margin.left,
          style: {
            left: `${left}px`,
            top: `${labelTop}px`,
            width: `${margin.left * this.scale}px`,
            height: `${labelHeight}px`,
          },
        },
      ].filter((band) => band.size > 0);
    },
    orderedElements() {
      return [...(this.definition.elementos || [])].sort(
        (first, second) => Number(first.ordem || 0) - Number(second.ordem || 0)
      );
    },
    renderedElements() {
      const convertedLegacy = Boolean(
        this.definition?.importacao_legado?.codigo
      );
      return this.orderedElements.flatMap((source) => {
        const parts = resolveElementsForPrintOptions(
          source,
          this.testOptions,
          convertedLegacy
        );
        return parts.map((part, index) => ({
          ...part,
          __previewResolved: true,
          __sourceId: source.id,
          __partIndex: index,
          __partCount: parts.length,
        }));
      });
    },
    xMarks() {
      const step = this.labelWidth > 100 ? 20 : 10;
      return Array.from(
        { length: Math.floor(this.labelWidth / step) + 1 },
        (_, index) => index * step
      );
    },
    yMarks() {
      const step = this.labelHeight > 100 ? 20 : 10;
      return Array.from(
        { length: Math.floor(this.labelHeight / step) + 1 },
        (_, index) => index * step
      );
    },
    stageStyle() {
      return {
        width: `${
          (this.pageMargins.left + this.labelWidth + this.pageMargins.right) *
            this.scale +
          58
        }px`,
        height: `${
          (this.pageMargins.top + this.labelHeight + this.pageMargins.bottom) *
            this.scale +
          58
        }px`,
      };
    },
    canvasStyle() {
      return {
        left: `${this.canvasOffset.left}px`,
        top: `${this.canvasOffset.top}px`,
        width: `${this.labelWidth * this.scale}px`,
        height: `${this.labelHeight * this.scale}px`,
        backgroundColor: this.label.fundo || "#FFFFFF",
        borderColor: this.label.cor_borda || "#000000",
        borderWidth: `${Number(this.label.borda_mm || 0) * this.scale}px`,
        borderRadius: `${
          Number(this.label.cantos_arredondados_mm || 0) * this.scale
        }px`,
        backgroundSize: [
          `${this.scale / 2}px ${this.scale / 2}px`,
          `${this.scale / 2}px ${this.scale / 2}px`,
          `${this.scale * 5}px ${this.scale * 5}px`,
          `${this.scale * 5}px ${this.scale * 5}px`,
        ].join(", "),
      };
    },
    safeAreaStyle() {
      const padding = this.safePadding * this.scale;
      return {
        top: `${padding}px`,
        right: `${padding}px`,
        bottom: `${padding}px`,
        left: `${padding}px`,
        display: padding > 0 ? "block" : "none",
      };
    },
    safePadding() {
      return Math.max(
        0,
        Math.min(
          Number(this.label.padding_mm || 0),
          this.labelWidth / 2,
          this.labelHeight / 2
        )
      );
    },
    rulerXStyle() {
      return {
        left: `${this.canvasOffset.left}px`,
        top: `${this.canvasOffset.top - 19}px`,
        width: `${this.labelWidth * this.scale}px`,
      };
    },
    rulerYStyle() {
      return {
        left: `${this.canvasOffset.left - 21}px`,
        top: `${this.canvasOffset.top}px`,
        height: `${this.labelHeight * this.scale}px`,
      };
    },
    codeAssetSignature() {
      return JSON.stringify(
        this.renderedElements
          .filter((element) =>
            ["barcode", "qrcode"].includes(this.previewElementType(element))
          )
          .map((element) => ({
            id: element.id,
            tipo: this.previewElementType(element),
            valor: this.rawValue(element),
            largura_mm: element.largura_mm,
            altura_mm: element.altura_mm,
            codigo_barras: element.codigo_barras || {},
            mostrar: this.elementContentVisible(element),
          }))
      );
    },
  },
  watch: {
    codeAssetSignature: {
      immediate: true,
      handler() {
        this.scheduleCodeAssets();
      },
    },
    exampleCompanyId: {
      immediate: true,
      handler() {
        this.loadLogo();
      },
    },
  },
  beforeDestroy() {
    if (this.codeAssetTimer) clearTimeout(this.codeAssetTimer);
    this.codeAssetGeneration += 1;
    this.logoGeneration += 1;
    this.removeDocumentListeners();
  },
  methods: {
    previewElement(element) {
      if (element?.__previewResolved) return element;
      return resolveElementForPrintOptions(
        element,
        this.testOptions,
        Boolean(this.definition?.importacao_legado?.codigo)
      );
    },
    previewElementType(element) {
      return this.previewElement(element)?.tipo || element.tipo;
    },
    elementClasses(element) {
      return {
        selected: this.selectedId === this.sourceElementId(element),
        hidden: element.visivel === false,
        "rule-hidden": !this.elementContentVisible(element),
        [`type-${element.tipo}`]: true,
      };
    },
    sourceElementId(element) {
      return element?.__sourceId || element?.id;
    },
    sourceElement(element) {
      const sourceId = this.sourceElementId(element);
      return (
        this.orderedElements.find((candidate) => candidate.id === sourceId) ||
        element
      );
    },
    isFirstRenderedPart(element) {
      return Number(element?.__partIndex || 0) === 0;
    },
    isLastRenderedPart(element) {
      return (
        Number(element?.__partIndex || 0) ===
        Number(element?.__partCount || 1) - 1
      );
    },
    elementStyle(element) {
      const style = element.estilo || {};
      const verticalMap = {
        top: "flex-start",
        middle: "center",
        bottom: "flex-end",
      };
      return {
        left: `${Number(element.x_mm || 0) * this.scale}px`,
        top: `${Number(element.y_mm || 0) * this.scale}px`,
        width: `${Number(element.largura_mm || 1) * this.scale}px`,
        height: `${Number(element.altura_mm || 1) * this.scale}px`,
        zIndex: Number(element.ordem || 0) + 2,
        overflow: "hidden",
        transform: `rotate(${Number(element.rotacao || 0)}deg)`,
        color: style.cor || "#000000",
        backgroundColor: style.fundo || "transparent",
        borderColor: style.cor_borda || "#000000",
        borderWidth: `${Number(style.borda_mm || 0) * this.scale}px`,
        padding: `${Number(style.padding_mm || 0) * this.scale}px`,
        fontFamily: this.fontFamily(style.fonte),
        fontSize: `${Number(style.tamanho_pt || 8) * 0.3528 * this.scale}px`,
        fontWeight: style.negrito ? 700 : 400,
        fontStyle: style.italico ? "italic" : "normal",
        textDecoration: style.sublinhado ? "underline" : "none",
        textAlign: style.alinhamento || "left",
        alignItems: verticalMap[style.alinhamento_vertical] || "center",
        letterSpacing: `${
          Number(style.espacamento_letras_pt || 0) * 0.3528 * this.scale
        }px`,
        whiteSpace: "normal",
        wordBreak: "normal",
        overflowWrap: "normal",
      };
    },
    linePreviewStyle(element) {
      const style = element.estilo || {};
      const requestedWidth = Number(style.borda_mm || 0.2);
      const widthMm = Math.max(
        0.1,
        Number.isFinite(requestedWidth) ? requestedWidth : 0.2
      );

      return {
        borderTop: `${widthMm * this.scale}px solid ${
          style.cor_borda || "#000000"
        }`,
      };
    },
    fontFamily(font) {
      return (
        {
          dejavu_serif:
            "'Etiqueta DejaVu Serif', 'DejaVu Serif Condensed', serif",
          dejavu_mono:
            "'Etiqueta DejaVu Sans Mono', 'DejaVu Sans Mono', monospace",
        }[font] || "'Etiqueta DejaVu Sans', 'DejaVu Sans', sans-serif"
      );
    },
    priceLines(element) {
      return priceDisplayLines(
        this.previewElement(element),
        this.resolvedValues
      );
    },
    priceLineStyle(element, line) {
      const style = element.estilo || {};
      const fontSize = Number(style.tamanho_pt || 8);
      const inset = Number(style.padding_mm || 0) + Number(style.borda_mm || 0);
      const width = Math.max(0.2, Number(element.largura_mm || 0) - 2 * inset);
      const estimatedWidth =
        Math.max(1, Array.from(line).length) * fontSize * 0.23;
      const lineCount = this.priceLines(element)?.length || 1;
      const height = Math.max(
        0.2,
        Number(element.altura_mm || 0) - 2 * inset - 0.5
      );
      const heightLimit = height / (lineCount * 0.3528 * 1.05);
      const fittedPt =
        Math.round(
          Math.max(
            4,
            Math.min(fontSize, (fontSize * width) / estimatedWidth, heightLimit)
          ) * 100
        ) / 100;
      return { fontSize: `${fittedPt * 0.3528 * this.scale}px` };
    },
    displayValue(element) {
      return previewValue(this.previewElement(element), this.resolvedValues);
    },
    rawValue(element) {
      const value = this.resolvedValues[this.previewElement(element)?.campo];
      return value === null || value === undefined ? "" : String(value);
    },
    elementContentVisible(element) {
      return shouldDisplayPreview(
        this.previewElement(element),
        this.resolvedValues,
        this.testOptions,
        this.logoUrl
      );
    },
    codeAssetUrl(element) {
      return this.codeAssets[element.id] || "";
    },
    codeAssetFailed(element) {
      return Boolean(this.codeAssetErrors[element.id]);
    },
    barcodeImageStyle(element) {
      const showText = Boolean(element.codigo_barras?.exibir_texto);
      return { maxHeight: showText ? "78%" : "100%" };
    },
    scheduleCodeAssets() {
      if (this.codeAssetTimer) clearTimeout(this.codeAssetTimer);
      this.codeAssetTimer = setTimeout(() => {
        this.codeAssetTimer = null;
        this.loadCodeAssets();
      }, 120);
    },
    async loadCodeAssets() {
      const generation = ++this.codeAssetGeneration;
      const elements = this.renderedElements.filter(
        (element) =>
          ["barcode", "qrcode"].includes(this.previewElementType(element)) &&
          this.elementContentVisible(element) &&
          this.rawValue(element) !== ""
      );
      const next = {};
      const errors = {};

      await Promise.all(
        elements.map(async (element) => {
          const payload = {
            tipo: this.previewElementType(element),
            valor: this.rawValue(element),
            largura_mm: Number(element.largura_mm),
            altura_mm: Number(element.altura_mm),
            codigo_barras: element.codigo_barras || {},
          };
          const key = JSON.stringify(payload);
          let asset = codeAssetCache.get(key);
          if (!asset) {
            asset = rememberAsset(
              codeAssetCache,
              key,
              etiquetasService
                .codigoImagem(payload)
                .then(({ data }) => blobToDataUrl(data))
            );
          }

          try {
            next[element.id] = await asset;
          } catch (error) {
            next[element.id] = "";
            errors[element.id] = true;
          }
        })
      );

      if (generation === this.codeAssetGeneration) {
        this.codeAssets = next;
        this.codeAssetErrors = errors;
      }
    },
    async loadLogo() {
      const generation = ++this.logoGeneration;
      const id = Number(this.exampleCompanyId || 0);
      this.logoUrl = "";
      this.companyValues = {};
      if (!id) return;

      let asset = logoAssetCache.get(id);
      if (!asset) {
        asset = rememberAsset(
          logoAssetCache,
          id,
          etiquetasService.logoImagem(id).then(({ data }) => ({
            url: String(data?.url || ""),
            values:
              data?.dados_etiqueta && typeof data.dados_etiqueta === "object"
                ? clone(data.dados_etiqueta)
                : {},
          })),
          30
        );
      }

      try {
        const companyAsset = await asset;
        if (generation === this.logoGeneration) {
          this.logoUrl = companyAsset.url;
          this.companyValues = companyAsset.values;
        }
      } catch (error) {
        if (generation === this.logoGeneration) {
          this.logoUrl = "";
          this.companyValues = {};
        }
      }
    },
    formatMillimeters(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    beginDrag(event, element) {
      const source = this.sourceElement(element);
      this.$emit("select", source.id);
      this.$emit("change-start");
      this.startInteraction("drag", event, source);
    },
    beginResize(event, element) {
      const source = this.sourceElement(element);
      this.$emit("select", source.id);
      this.$emit("change-start");
      this.startInteraction("resize", event, source);
    },
    startInteraction(type, event, element) {
      this.interaction = {
        type,
        startX: event.clientX,
        startY: event.clientY,
        element: clone(element),
      };
      document.addEventListener("mousemove", this.onPointerMove);
      document.addEventListener("mouseup", this.endInteraction);
    },
    onPointerMove(event) {
      if (!this.interaction) return;
      const source = this.interaction.element;
      const next = clone(source);
      const step = event.altKey ? 0.1 : 0.5;
      const deltaX = (event.clientX - this.interaction.startX) / this.scale;
      const deltaY = (event.clientY - this.interaction.startY) / this.scale;

      if (this.interaction.type === "drag") {
        const maxX = Math.max(0, this.labelWidth - Number(source.largura_mm));
        const maxY = Math.max(0, this.labelHeight - Number(source.altura_mm));
        next.x_mm = this.snap(
          Math.max(0, Math.min(maxX, Number(source.x_mm) + deltaX)),
          step
        );
        next.y_mm = this.snap(
          Math.max(0, Math.min(maxY, Number(source.y_mm) + deltaY)),
          step
        );
      } else {
        const maxWidth = Math.max(0.5, this.labelWidth - Number(source.x_mm));
        const maxHeight = Math.max(0.5, this.labelHeight - Number(source.y_mm));
        next.largura_mm = this.snap(
          Math.max(0.5, Math.min(maxWidth, Number(source.largura_mm) + deltaX)),
          step
        );
        next.altura_mm = this.snap(
          Math.max(0.5, Math.min(maxHeight, Number(source.altura_mm) + deltaY)),
          step
        );
      }
      this.$emit("update", next);
    },
    endInteraction() {
      this.interaction = null;
      this.removeDocumentListeners();
      this.$emit("change-end");
    },
    removeDocumentListeners() {
      document.removeEventListener("mousemove", this.onPointerMove);
      document.removeEventListener("mouseup", this.endInteraction);
    },
    snap(value, step) {
      return Number((Math.round(value / step) * step).toFixed(2));
    },
  },
};
</script>

<style lang="scss" scoped>
@font-face {
  font-family: "Etiqueta DejaVu Sans";
  src: url("../assets/fonts/DejaVuSans.ttf") format("truetype");
  font-style: normal;
  font-weight: 400;
  font-display: swap;
}

@font-face {
  font-family: "Etiqueta DejaVu Sans";
  src: url("../assets/fonts/DejaVuSans-Bold.ttf") format("truetype");
  font-style: normal;
  font-weight: 700;
  font-display: swap;
}

@font-face {
  font-family: "Etiqueta DejaVu Sans";
  src: url("../assets/fonts/DejaVuSans-Oblique.ttf") format("truetype");
  font-style: italic;
  font-weight: 400;
  font-display: swap;
}

@font-face {
  font-family: "Etiqueta DejaVu Sans";
  src: url("../assets/fonts/DejaVuSans-BoldOblique.ttf") format("truetype");
  font-style: italic;
  font-weight: 700;
  font-display: swap;
}

@font-face {
  font-family: "Etiqueta DejaVu Serif";
  src: url("../assets/fonts/DejaVuSerif.ttf") format("truetype");
  font-style: normal;
  font-weight: 400;
  font-display: swap;
}

@font-face {
  font-family: "Etiqueta DejaVu Serif";
  src: url("../assets/fonts/DejaVuSerif-Bold.ttf") format("truetype");
  font-style: normal;
  font-weight: 700;
  font-display: swap;
}

@font-face {
  font-family: "Etiqueta DejaVu Serif";
  src: url("../assets/fonts/DejaVuSerif-Italic.ttf") format("truetype");
  font-style: italic;
  font-weight: 400;
  font-display: swap;
}

@font-face {
  font-family: "Etiqueta DejaVu Serif";
  src: url("../assets/fonts/DejaVuSerif-BoldItalic.ttf") format("truetype");
  font-style: italic;
  font-weight: 700;
  font-display: swap;
}

@font-face {
  font-family: "Etiqueta DejaVu Sans Mono";
  src: url("../assets/fonts/DejaVuSansMono.ttf") format("truetype");
  font-style: normal;
  font-weight: 400;
  font-display: swap;
}

@font-face {
  font-family: "Etiqueta DejaVu Sans Mono";
  src: url("../assets/fonts/DejaVuSansMono-Bold.ttf") format("truetype");
  font-style: normal;
  font-weight: 700;
  font-display: swap;
}

@font-face {
  font-family: "Etiqueta DejaVu Sans Mono";
  src: url("../assets/fonts/DejaVuSansMono-Oblique.ttf") format("truetype");
  font-style: italic;
  font-weight: 400;
  font-display: swap;
}

@font-face {
  font-family: "Etiqueta DejaVu Sans Mono";
  src: url("../assets/fonts/DejaVuSansMono-BoldOblique.ttf") format("truetype");
  font-style: italic;
  font-weight: 700;
  font-display: swap;
}

.label-workspace {
  min-height: 520px;
  border-radius: 12px;
  background: #f3f6f9;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.workspace-toolbar,
.workspace-help {
  min-height: 45px;
  padding: 0 16px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #5e6278;
  font-size: 11px;
  border-bottom: 1px solid #e4e6ef;
}

.workspace-help {
  min-height: 40px;
  border-top: 1px solid #e4e6ef;
  border-bottom: 0;
  justify-content: flex-start;
  gap: 20px;

  i {
    color: #8950fc;
    margin-right: 4px;
  }

  kbd {
    padding: 1px 4px;
    border: 1px solid #d8dbe5;
    border-bottom-width: 2px;
    border-radius: 4px;
    background: #fff;
    color: #5e6278;
    font-size: 9px;
  }
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #1bc5bd;
  box-shadow: 0 0 0 4px rgba(27, 197, 189, 0.13);
  margin-right: 10px;
}

.canvas-scroll {
  flex: 1;
  min-height: 430px;
  overflow: auto;
  padding: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #eef1f5;
  background-image: radial-gradient(#d8dce6 0.7px, transparent 0.7px);
  background-size: 16px 16px;
}

.canvas-stage {
  position: relative;
  flex: 0 0 auto;
}

.label-canvas {
  position: absolute;
  left: 40px;
  top: 30px;
  box-sizing: border-box;
  border-style: solid;
  box-shadow: 0 12px 30px rgba(31, 45, 61, 0.16),
    0 2px 6px rgba(31, 45, 61, 0.1);
  overflow: hidden;
  z-index: 2;

  &.has-grid {
    background-image: linear-gradient(
        to right,
        rgba(111, 66, 193, 0.2) 1px,
        transparent 1px
      ),
      linear-gradient(to bottom, rgba(111, 66, 193, 0.2) 1px, transparent 1px),
      linear-gradient(to right, rgba(54, 153, 255, 0.42) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(54, 153, 255, 0.42) 1px, transparent 1px);
  }
}

.page-margin-band {
  position: absolute;
  z-index: 1;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid rgba(94, 98, 120, 0.18);
  background: repeating-linear-gradient(
    135deg,
    #d4d7df 0,
    #d4d7df 6px,
    #c9cdd6 6px,
    #c9cdd6 12px
  );
  color: #555b6e;
  pointer-events: none;

  span {
    position: absolute;
    left: 50%;
    top: 50%;
    padding: 1px 5px;
    border-radius: 3px;
    background: rgba(255, 255, 255, 0.78);
    font: 600 8px/1.4 Arial, sans-serif;
    white-space: nowrap;
    transform: translate(-50%, -50%);
  }

  &.margin-left span,
  &.margin-right span {
    transform: translate(-50%, -50%) rotate(-90deg);
  }
}

.safe-area {
  position: absolute;
  pointer-events: none;
  border: 1px dashed rgba(54, 153, 255, 0.55);
  z-index: 1001;
}

.safe-area-shade {
  position: absolute;
  pointer-events: none;
  box-shadow: 0 0 0 9999px rgba(94, 98, 120, 0.22);
  z-index: 1;
}

.ruler {
  position: absolute;
  color: #8a8f9f;
  font-size: 9px;
  pointer-events: none;
  z-index: 4;

  span {
    position: absolute;
  }
}

.ruler-x {
  left: 40px;
  top: 11px;
  height: 17px;
  border-bottom: 1px solid #cbd0dc;

  span {
    bottom: 3px;
    transform: translateX(-50%);

    &::after {
      content: "";
      position: absolute;
      left: 50%;
      top: 12px;
      height: 5px;
      border-left: 1px solid #aeb4c2;
    }
  }
}

.ruler-y {
  left: 19px;
  top: 30px;
  width: 20px;
  border-right: 1px solid #cbd0dc;

  span {
    right: 4px;
    transform: translateY(-50%) rotate(-90deg);

    &::after {
      content: "";
      position: absolute;
      left: 50%;
      top: 12px;
      height: 5px;
      border-left: 1px solid #aeb4c2;
    }
  }
}

.label-element {
  position: absolute;
  box-sizing: border-box;
  border-style: solid;
  display: flex;
  justify-content: flex-start;
  overflow: hidden;
  cursor: move;
  user-select: none;
  line-height: 1.05;

  &:hover {
    outline: 1px dashed rgba(137, 80, 252, 0.65);
  }

  &.selected {
    outline: 2px solid #8950fc;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.85);
  }

  &.hidden {
    opacity: 0.28;
    filter: grayscale(1);
  }

  &.rule-hidden:not(.selected) {
    opacity: 0;
    pointer-events: none;
  }

  &.rule-hidden.selected {
    opacity: 0.32;
  }

  &.type-barcode,
  &.type-qrcode,
  &.type-logo {
    align-items: center !important;
    justify-content: center;
    flex-direction: column;
  }

  &.type-line {
    align-items: center !important;
  }

  &.type-rectangle {
    align-items: center !important;
    justify-content: center;
  }
}

.element-text {
  display: block;
  width: 100%;
  overflow: hidden;
  word-break: normal;
  overflow-wrap: normal;
  white-space: normal;
}

.element-text-line {
  display: block;
  white-space: nowrap;
}

.code-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
}

.code-image {
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  max-width: 100%;
  object-fit: contain;
}

.code-preview-error {
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  border: 1px dashed #e4a11b;
  color: #8a6512;
  background: rgba(255, 244, 204, 0.82);
  font: 600 8px/1.2 Arial, sans-serif;
  text-align: center;
}

.label-element.type-barcode small {
  flex: 0 0 auto;
  width: 100%;
  text-align: center;
  font-family: inherit;
  font-size: 1em;
  line-height: 1.05;
  letter-spacing: inherit;
  white-space: nowrap;
}

.qr-preview {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.logo-preview {
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.line-preview {
  display: block;
  width: 100%;
}

.rectangle-caption {
  opacity: 0.22;
  font-size: 0.65em;
  letter-spacing: 0.08em;
}

.selection-name {
  position: absolute;
  left: 1px;
  top: 1px;
  max-width: calc(100% - 2px);
  padding: 3px 6px;
  border-radius: 4px 4px 4px 0;
  background: #8950fc;
  color: #fff;
  font: 600 10px/1.2 Arial, sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transform: none;
}

.resize-handle {
  position: absolute;
  right: 1px;
  bottom: 1px;
  width: 11px;
  height: 11px;
  border: 2px solid #fff;
  border-radius: 2px;
  background: #8950fc;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  cursor: nwse-resize;
}

.hidden-label {
  position: absolute;
  right: 2px;
  top: 2px;
  padding: 1px 3px;
  border-radius: 3px;
  background: #3f4254;
  color: #fff;
  font: 600 7px/1 Arial, sans-serif;
}

@media (max-width: 991.98px) {
  .workspace-help span:nth-child(n + 3) {
    display: none;
  }

  .canvas-scroll {
    justify-content: flex-start;
  }
}
</style>
